import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, API, authFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocations } from "@/context/LocationContext";
import { useKiosk } from "@/context/KioskContext";
import { toast } from "sonner";
import {
  CheckCircle2, AlertTriangle, Clock, ShieldCheck, Camera, QrCode,
  Calendar, ChevronDown, ChevronRight, History, Download, UserCog, Flag, Pencil,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, Panel } from "@/components/ui-bits";
import { CleaningQR } from "@/components/CleaningQR";
import SupervisorPinModal from "@/components/SupervisorPinModal";
import { devLog } from "@/lib/devLog";

const FREQ_LABEL = { daily: "Daily", weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", annual: "Annual" };

function complianceTone(pct) {
  if (pct >= 95) return "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40";
  if (pct >= 75) return "border-amber-300 bg-amber-50 dark:bg-amber-950/40";
  return "border-rose-300 bg-rose-50 dark:bg-rose-950/40";
}

function dueTone(days) {
  if (days < 0) return "text-rose-700 font-bold";
  if (days <= 7) return "text-amber-700 font-bold";
  return "text-stone-700 dark:text-stone-200 font-semibold";
}

function historyStatus(r) {
  if (r.missed) return "missed";
  return (r.completed ?? true) ? "completed" : "incomplete";
}

const CATEGORY_LABELS = {
  production: "Production",
  packing: "Packing",
  baking: "Baking",
  non_production: "Non-Production",
  cake_baklava: "Cake & Baklava",
  deep_cleaning: "Deep Cleaning",
};

// Approved cleaning chemicals on site. Operators pick from this list at task
// completion so the COSHH audit trail is consistent and free of typos.
const CLEANING_PRODUCTS = [
  "Spray & Wipe Sanitiser",
  "Food Safe Degreaser / Detergent",
  "Maximum Eco Floor Maintainer",
  "Enzyme Drain Maintainer",
  "Oven Cleaner (Caustic)",
  "Heavy Duty Mould & Mildew Remover",
  "Purple Maximum Toilet Descaler",
  "Citrus Ultra 4-in-1 Descaling Liquid",
  "Antibacterial Handwash",
  "Glass & Stainless Cleaner",
];

const FREQUENCIES = [
  { id: "daily",     label: "Today" },
  { id: "weekly",    label: "Week" },
  { id: "monthly",   label: "Month" },
  { id: "quarterly", label: "Quarter" },
  { id: "annual",    label: "Annual" },
];
const DEEP_FREQUENCIES = new Set(["monthly", "quarterly", "annual"]);

const STATUS_STYLE = {
  verified: {
    label: "Verified",
    cls: "bg-emerald-100 text-emerald-900 border-emerald-300",
    dot: "bg-emerald-500",
  },
  completed: {
    label: "Completed",
    cls: "bg-emerald-50 text-emerald-800 border-emerald-200",
    dot: "bg-emerald-400",
  },
  pending_verification: {
    label: "Pending verify",
    cls: "bg-sky-50 text-sky-900 border-sky-300",
    dot: "bg-sky-500",
  },
  upcoming: {
    label: "Upcoming",
    cls: "bg-stone-100 text-stone-700 border-stone-300",
    dot: "bg-stone-400",
  },
  due: {
    label: "Due",
    cls: "bg-amber-50 text-amber-900 border-amber-300",
    dot: "bg-amber-500",
  },
  overdue: {
    label: "Overdue",
    cls: "bg-rose-50 text-rose-900 border-rose-300",
    dot: "bg-rose-500",
  },
  missed: {
    label: "Missed",
    cls: "bg-rose-100 text-rose-900 border-rose-400",
    dot: "bg-rose-600",
  },
  incomplete: {
    label: "Incomplete",
    cls: "bg-amber-100 text-amber-900 border-amber-400",
    dot: "bg-amber-600",
  },
};

function ComplianceCards({ data, frequency }) {
  if (!data) return null;
  const isDeep = DEEP_FREQUENCIES.has(frequency);
  // For deep-cleaning tabs, show a 3-card breakdown by FREQUENCY (Monthly /
  // Quarterly / Annual) rather than by area category — the user thinks of
  // these as separate audit categories.
  if (isDeep) {
    const dcRow = data.categories?.deep_cleaning || {};
    return (
      <div className="grid grid-cols-3 gap-3" data-testid="compliance-cards">
        {["monthly", "quarterly", "annual"].map((f) => {
          const row = dcRow[f] || { total: 0, done: 0, percent: 100 };
          const pct = row.percent;
          const tone = complianceTone(pct);
          return (
            <div
              key={f}
              className={`rounded-xl border ${tone} p-4`}
              data-testid={`compliance-${f}`}
            >
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-600 dark:text-stone-300">
                {FREQ_LABEL[f]}
              </div>
              <div className="text-3xl font-display font-bold tnum mt-1">{pct}%</div>
              <div className="text-xs text-stone-500 mt-1 tnum">
                {row.done}/{row.total} tasks on schedule
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  // Daily/weekly: per-area compliance.
  const categories = ["production", "packing", "baking", "non_production", "cake_baklava"];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="compliance-cards">
      {categories.map((key) => {
        const label = CATEGORY_LABELS[key];
        const row = data.categories?.[key]?.[frequency] || { total: 0, done: 0, percent: 100 };
        const pct = row.percent;
        const tone = complianceTone(pct);
        return (
          <div
            key={key}
            className={`rounded-xl border ${tone} p-4`}
            data-testid={`compliance-${key}`}
          >
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-600 dark:text-stone-300">
              {label}
            </div>
            <div className="text-3xl font-display font-bold tnum mt-1">{pct}%</div>
            <div className="text-xs text-stone-500 mt-1 tnum">
              {row.done}/{row.total} tasks
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusPill({ status, testId }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.due;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${s.cls}`}
      data-testid={testId}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

function TaskRow({ it, isDeep, onComplete, onVerify, onShowQR }) {
  return (
    <div
      className="px-4 py-3 flex flex-wrap items-center gap-3"
      data-testid={`task-row-${it.task.id}`}
    >
      <div className="min-w-[14rem] flex-1">
        <div className="font-semibold">{it.task.area_name}</div>
        {it.task.equipment_name && (
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mt-0.5">
            {it.task.equipment_name}
          </div>
        )}
        {isDeep && it.task.instructions && (
          <div className="text-xs text-stone-600 dark:text-stone-300 mt-1 leading-snug max-w-[40rem]">
            {it.task.instructions}
          </div>
        )}
        <div className="text-[11px] text-stone-500 mt-1 tnum">
          {it.latest_record?.performed_at && (
            <>
              Last:{" "}
              {it.latest_record.performed_at.slice(0, 10)}
              {it.latest_record.performed_by_name && ` · ${it.latest_record.performed_by_name}`}
            </>
          )}
          {isDeep && it.next_due && (
            <span className="ml-2">
              · Next due{" "}
              <span
                className={dueTone(it.days_until_due)}
              >
                {it.next_due.slice(0, 10)}
              </span>
              {it.days_until_due >= 0 && (
                <span className="text-stone-500">
                  {" "}({it.days_until_due}d)
                </span>
              )}
              {it.days_until_due < 0 && (
                <span className="text-rose-700">
                  {" "}({Math.abs(it.days_until_due)}d overdue)
                </span>
              )}
            </span>
          )}
        </div>
      </div>
      <StatusPill status={it.status} testId={`status-${it.task.id}`} />
      <div className="flex gap-2">
        {(it.status === "completed" || it.status === "pending_verification") && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onVerify(it)}
            data-testid={`verify-${it.task.id}`}
            className="h-9"
          >
            <ShieldCheck className="h-4 w-4 mr-1" /> Verify
          </Button>
        )}
        <Button
          size="sm"
          variant={
            it.status === "due" || it.status === "overdue" ? "default" : "outline"
          }
          onClick={() => onComplete(it)}
          data-testid={`complete-${it.task.id}`}
          className="h-9"
        >
          <CheckCircle2 className="h-4 w-4 mr-1" />
          {["due", "overdue"].includes(it.status) ? "Complete" : "Re-clean"}
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={() => onShowQR(it)}
          data-testid={`qr-${it.task.id}`}
          title="QR code for this equipment"
          className="h-9 w-9"
        >
          <QrCode className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function SubAreaHeader({ category, subArea, items, isAdmin, onAssigned }) {
  const done = items.filter((i) => !["due", "overdue"].includes(i.status)).length;
  // Assigned-to lives on the task template. All tasks in a sub-area share the
  // same assignee via the /cleaning/sub-areas/assign bulk endpoint, so we
  // read it off the first task and show a single badge for the section.
  const assignedTo = items.find((i) => i.task?.assigned_to)?.task?.assigned_to || "";
  const [saving, setSaving] = useState(false);

  const handleAssign = async () => {
    const next = window.prompt(
      `Assign "${subArea}" to (leave blank to un-assign):`,
      assignedTo || ""
    );
    if (next === null) return;
    setSaving(true);
    try {
      await api.post("/cleaning/sub-areas/assign", {
        category,
        sub_area: subArea,
        frequency: items[0]?.task?.frequency,
        assigned_to: next.trim(),
      });
      toast.success(next.trim() ? `Assigned to ${next.trim()}` : "Assignment cleared");
      onAssigned?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="px-4 py-2 bg-stone-50 dark:bg-stone-800/40 border-t border-b border-border dark:border-stone-700 flex items-center justify-between gap-2"
      data-testid={`sub-area-header-${subArea.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-bold">
          Section
        </span>
        <span className="font-display font-bold text-sm">{subArea}</span>
        <span className="text-[11px] text-stone-500 tnum">
          {done}/{items.length}
        </span>
        {assignedTo ? (
          <span
            className="inline-flex items-center gap-1 px-2 h-6 rounded-full bg-sky-100 text-sky-900 text-[11px] font-semibold"
            data-testid={`sub-area-assignee-${subArea.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          >
            <UserCog className="h-3 w-3" /> {assignedTo}
          </span>
        ) : (
          <span className="text-[11px] text-stone-500 italic">Unassigned</span>
        )}
      </div>
      {isAdmin && (
        <Button
          size="sm"
          variant="ghost"
          onClick={handleAssign}
          disabled={saving}
          className="h-7 text-xs"
          data-testid={`sub-area-assign-${subArea.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        >
          <UserCog className="h-3 w-3 mr-1" /> Assign
        </Button>
      )}
    </div>
  );
}

function CategoryGroup({
  category, items, onComplete, onVerify, onShowQR, isDeep, isAdmin, onAssigned,
}) {
  const [open, setOpen] = useState(true);
  const done = items.filter(
    (i) => !["due", "overdue"].includes(i.status)
  ).length;

  // Sub-group by `task.sub_area` when at least one task in this category
  // carries a sub_area. Tasks without a sub_area fall into "General".
  const hasSubAreas = items.some((i) => (i.task?.sub_area || "").trim());
  const grouped = hasSubAreas
    ? items.reduce((acc, it) => {
        const key = (it.task?.sub_area || "").trim() || "General";
        (acc[key] ||= []).push(it);
        return acc;
      }, {})
    : null;
  // Deterministic order: sub_areas in the order they first appear in `items`,
  // "General" always last if present.
  const subAreaOrder = hasSubAreas
    ? [
        ...Array.from(
          new Set(
            items
              .map((i) => (i.task?.sub_area || "").trim())
              .filter(Boolean)
          )
        ),
        ...(grouped.General ? ["General"] : []),
      ]
    : [];

  return (
    <div className="rounded-xl border border-border dark:border-stone-700 overflow-hidden mb-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-4 py-3 bg-stone-50/60 dark:bg-stone-800/60 flex items-center justify-between"
        data-testid={`category-toggle-${category}`}
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-display font-bold text-base">{CATEGORY_LABELS[category]}</span>
          <span className="text-xs text-stone-500 tnum">
            {done}/{items.length} on schedule
          </span>
        </div>
      </button>
      {open && (
        <div className="divide-y divide-border dark:divide-stone-700">
          {hasSubAreas
            ? subAreaOrder.map((sa) => (
                <div key={sa}>
                  <SubAreaHeader
                    category={category}
                    subArea={sa}
                    items={grouped[sa]}
                    isAdmin={isAdmin}
                    onAssigned={onAssigned}
                  />
                  <div className="divide-y divide-border dark:divide-stone-700">
                    {grouped[sa].map((it) => (
                      <TaskRow
                        key={it.task.id}
                        it={it}
                        isDeep={isDeep}
                        onComplete={onComplete}
                        onVerify={onVerify}
                        onShowQR={onShowQR}
                      />
                    ))}
                  </div>
                </div>
              ))
            : items.map((it) => (
                <TaskRow
                  key={it.task.id}
                  it={it}
                  isDeep={isDeep}
                  onComplete={onComplete}
                  onVerify={onVerify}
                  onShowQR={onShowQR}
                />
              ))}
        </div>
      )}
    </div>
  );
}

function CompleteDialog({ open, item, onClose, onSubmit, record = null }) {
  const { user } = useAuth();
  const { locations, selected } = useLocations();
  const kiosk = useKiosk();
  const [form, setForm] = useState(null);
  const [photoData, setPhotoData] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open && item) {
      // In edit mode (record supplied) we prefill from the existing row so
      // the operator can finish off a "Not completed" entry without losing
      // any of the previously typed context.
      if (record) {
        setForm({
          completed: !!record.completed,
          products_used: record.products_used || "",
          issues_found: record.issues_found || "",
          corrective_action: record.corrective_action || "",
          issue_category: record.issue_category || "",
          notes: record.notes || "",
          location_id: record.location_id || selected || "",
          signed_by: record.signed_by || user?.name || "",
        });
        setPhotoData(record.photo_data_url || "");
      } else {
        setForm({
          completed: true,
          products_used: "",
          issues_found: "",
          corrective_action: "",
          issue_category: "",
          notes: "",
          location_id: selected || "",
          signed_by: user?.name || "",
        });
        setPhotoData("");
      }
    }
  }, [open, item, record, selected, user?.name]);

  // Compress + downscale large photos client-side so floor staff can
  // use the tablet's native camera (typically 4–8 MB images) without
  // hitting the backend payload limit. We target ~800 KB JPEG, 1600 px
  // max edge, quality 0.82 — visually identical for cleaning evidence
  // but easily under the API's 1 MB request cap.
  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = () => {
        img.onload = () => {
          const maxEdge = 1600;
          const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, w, h);
          // Iteratively drop quality if the output is still too big
          // (very rare after the 1600 px downscale, but defends against
          // ultra-noisy JPEGs from older Android cameras).
          let quality = 0.82;
          let dataUrl = canvas.toDataURL("image/jpeg", quality);
          while (dataUrl.length > 900_000 && quality > 0.4) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL("image/jpeg", quality);
          }
          resolve(dataUrl);
        };
        img.onerror = () => reject(new Error("Unsupported image format"));
        img.src = reader.result;
      };
      reader.onerror = () => reject(reader.error || new Error("Read failed"));
      reader.readAsDataURL(file);
    });

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Hard cap on input size: 15 MB lets phone-native HEIC + JPEG cameras
    // through without clipping common photo dimensions.
    if (f.size > 15_000_000) {
      toast.error("Photo too large (max 15 MB raw)");
      return;
    }
    try {
      const compressed = await compressImage(f);
      setPhotoData(compressed);
      toast.success("Photo attached", { duration: 1200 });
    } catch (err) {
      devLog("[cleaning] photo compress failed:", err);
      toast.error("Could not process this photo — try a different file");
    }
  };

  if (!item || !form) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="cleaning-complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {item.task.area_name}
            <div className="text-xs text-stone-500 font-normal mt-1">
              {CATEGORY_LABELS[item.task.category]} ·{" "}
              {FREQ_LABEL[item.task.frequency] || "Daily"}
              {item.task.equipment_name && ` · ${item.task.equipment_name}`}
            </div>
          </DialogTitle>
        </DialogHeader>
        {item.task.instructions && (
          <div
            className="rounded-lg bg-stone-50 dark:bg-stone-800 border border-border dark:border-stone-700 px-3 py-2 text-sm text-stone-700 dark:text-stone-200 leading-snug"
            data-testid="cleaning-procedure"
          >
            <span className="font-bold uppercase text-[10px] tracking-wider text-stone-500 dark:text-stone-400 mr-2">
              Procedure
            </span>
            {item.task.instructions}
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (form.completed && !(form.products_used || "").trim()) {
              toast.error("Product used is required to complete a task");
              return;
            }
            onSubmit({
              ...form,
              task_id: item.task.id,
              area: item.task.area_name,
              photo_data_url: photoData || null,
              location_id: form.location_id || null,
            });
          }}
          className="space-y-3"
        >
          <div className="flex items-center gap-2">
            <input
              id="completed-cb"
              type="checkbox"
              checked={form.completed}
              onChange={(e) => setForm({ ...form, completed: e.target.checked })}
              data-testid="cleaning-completed-cb"
              className="h-5 w-5"
            />
            <Label htmlFor="completed-cb" className="font-semibold">
              Cleaning fully completed
            </Label>
          </div>
          <div>
            <Label>
              Product used <span className="text-rose-600" aria-hidden="true">*</span>
            </Label>
            <Select
              value={form.products_used || ""}
              onValueChange={(v) => setForm({ ...form, products_used: v })}
            >
              <SelectTrigger
                className={`h-11 mt-1 ${
                  form.completed && !(form.products_used || "").trim()
                    ? "border-rose-400 focus:ring-rose-400"
                    : ""
                }`}
                data-testid="cleaning-products"
                aria-required="true"
                aria-invalid={form.completed && !(form.products_used || "").trim()}
              >
                <SelectValue placeholder="Select a cleaning product (required)" />
              </SelectTrigger>
              <SelectContent>
                {CLEANING_PRODUCTS.map((p) => (
                  <SelectItem
                    key={p}
                    value={p}
                    data-testid={`cleaning-product-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  >
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.completed && !(form.products_used || "").trim() && (
              <div
                className="text-xs text-rose-600 mt-1"
                data-testid="cleaning-products-error"
              >
                Product used is required to complete a cleaning task.
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Issue category</Label>
              <Select
                value={form.issue_category || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, issue_category: v === "none" ? "" : v })
                }
              >
                <SelectTrigger className="h-11 mt-1" data-testid="cleaning-issue-cat">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="equipment_damaged">Equipment damaged</SelectItem>
                  <SelectItem value="not_completed">Cleaning not completed</SelectItem>
                  <SelectItem value="chemical_unavailable">Chemical unavailable</SelectItem>
                  <SelectItem value="hygiene">Hygiene issue</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location</Label>
              <Select
                value={form.location_id || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, location_id: v === "none" ? "" : v })
                }
              >
                <SelectTrigger className="h-11 mt-1" data-testid="cleaning-location">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Any —</SelectItem>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Issues found</Label>
            <Textarea
              rows={2}
              value={form.issues_found}
              onChange={(e) => setForm({ ...form, issues_found: e.target.value })}
              data-testid="cleaning-issues"
            />
          </div>
          <div>
            <Label>Corrective action taken</Label>
            <Textarea
              rows={2}
              value={form.corrective_action}
              onChange={(e) => setForm({ ...form, corrective_action: e.target.value })}
              data-testid="cleaning-corrective"
            />
          </div>
          <div>
            <Label>Photo evidence (optional)</Label>
            <div className="mt-1 flex items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhoto}
                className="hidden"
                data-testid="cleaning-photo-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  // The Android camera intent briefly hides the tab and
                  // drops fullscreen — tell KioskProvider to hold off
                  // its escape-detection for 90s while the operator
                  // snaps a photo (iteration_23 bug #2).
                  kiosk?.suspend?.(90_000);
                  fileRef.current?.click();
                }}
                className="h-11"
                data-testid="cleaning-photo-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                {photoData ? "Replace photo" : "Add photo"}
              </Button>
              {photoData && (
                <img
                  src={photoData}
                  alt="cleaning evidence"
                  className="h-11 w-11 rounded object-cover border border-border"
                  data-testid="cleaning-photo-preview"
                />
              )}
            </div>
          </div>
          <div>
            <Label>Signed by</Label>
            <Input
              className="h-11 mt-1"
              value={form.signed_by}
              onChange={(e) => setForm({ ...form, signed_by: e.target.value })}
              data-testid="cleaning-signed-by"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="h-12 px-6"
              data-testid="cleaning-submit"
              disabled={form.completed && !(form.products_used || "").trim()}
            >
              Submit record
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QRDialog({ open, item, onClose }) {
  if (!item) return null;
  const url = `${window.location.origin}/cleaning?task=${item.task.id}`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="cleaning-qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <QrCode className="h-5 w-5" /> {item.task.area_name}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 py-2">
          <CleaningQR value={url} size={220} testId="cleaning-qr-canvas" />
          <p className="text-xs text-stone-500 text-center">
            Stick this on the equipment. Staff can scan it with the bakery
            scanner or any phone camera to jump straight to this cleaning task.
          </p>
          <Button
            variant="outline"
            onClick={() => window.print()}
            data-testid="cleaning-qr-print"
            className="h-10"
          >
            Print
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HistoryView({ defaultLocation }) {
  const [filters, setFilters] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    return {
      from_date: monthAgo,
      to_date: today,
      category: "",
      frequency: "",
      status: "all", // all | completed | incomplete
    };
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Photo modal — null when closed; holds a data: URL when an
  // operator clicks a thumbnail in the table.
  const [photoModal, setPhotoModal] = useState(null);
  // Supervisor PIN modal for verifying a historical row.
  const [verifyRow, setVerifyRow] = useState(null);
  // Edit-in-place: the record currently being amended (typically an
  // incomplete entry). Wraps the shared CompleteDialog in PATCH mode.
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = {};
      if (filters.from_date) q.from_date = filters.from_date;
      if (filters.to_date) q.to_date = filters.to_date;
      if (filters.category) q.category = filters.category;
      if (filters.frequency) q.frequency = filters.frequency;
      if (defaultLocation) q.location_id = defaultLocation;
      const { data } = await api.get("/cleaning/history", { params: q });
      setRows(data.records || []);
    } catch (err) {
      devLog("[cleaning] failed to load history:", err);
      toast.error("Could not load history");
    } finally {
      setLoading(false);
    }
  }, [filters, defaultLocation]);

  useEffect(() => {
    load();
  }, [load]);

  // Client-side status filter — the backend returns real records +
  // synthetic "missed" rows for days/weeks that had no coverage.
  const filteredRows = useMemo(() => {
    if (filters.status === "completed")
      return rows.filter((r) => !r.missed && (r.completed ?? true) === true);
    if (filters.status === "incomplete")
      return rows.filter((r) => !r.missed && (r.completed ?? true) === false);
    if (filters.status === "missed") return rows.filter((r) => r.missed);
    return rows;
  }, [rows, filters.status]);

  const exportCsv = async () => {
    const q = new URLSearchParams();
    if (filters.from_date) q.set("from_date", filters.from_date);
    if (filters.to_date) q.set("to_date", filters.to_date);
    if (filters.category) q.set("category", filters.category);
    if (filters.frequency) q.set("frequency", filters.frequency);
    if (defaultLocation) q.set("location_id", defaultLocation);
    try {
      const r = await authFetch(`${API}/cleaning/history.csv?${q.toString()}`);
      if (!r.ok) throw new Error("export failed");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cleaning-history-${filters.from_date || "start"}_to_${filters.to_date || "today"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length} record${rows.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div data-testid="cleaning-history-view">
      <div className="rounded-xl border border-border dark:border-stone-700 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col">
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1">From</label>
          <Input
            type="date"
            value={filters.from_date}
            onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
            className="h-11 w-44"
            data-testid="history-from-date"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1">To</label>
          <Input
            type="date"
            value={filters.to_date}
            onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
            className="h-11 w-44"
            data-testid="history-to-date"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1">Category</label>
          <Select
            value={filters.category || "all"}
            onValueChange={(v) => setFilters({ ...filters, category: v === "all" ? "" : v })}
          >
            <SelectTrigger className="h-11 w-44" data-testid="history-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col">
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1">Frequency</label>
          <Select
            value={filters.frequency || "all"}
            onValueChange={(v) => setFilters({ ...filters, frequency: v === "all" ? "" : v })}
          >
            <SelectTrigger className="h-11 w-40" data-testid="history-frequency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All frequencies</SelectItem>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col">
          <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-1">Status</label>
          <Select
            value={filters.status || "all"}
            onValueChange={(v) => setFilters({ ...filters, status: v })}
          >
            <SelectTrigger className="h-11 w-40" data-testid="history-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All records</SelectItem>
              <SelectItem value="completed">Completed only</SelectItem>
              <SelectItem value="incomplete">Incomplete only</SelectItem>
              <SelectItem value="missed">Missed only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={exportCsv}
          disabled={loading || filteredRows.length === 0}
          className="h-11 gap-2"
          data-testid="history-export"
        >
          <Download className="h-4 w-4" /> Export CSV ({filteredRows.length})
        </Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-stone-500">Loading history…</div>
      ) : filteredRows.length === 0 ? (
        <div className="py-10 text-center text-stone-500" data-testid="history-empty">
          {rows.length === 0
            ? "No cleaning records in this window."
            : "No records match the selected status filter."}
        </div>
      ) : (
        <div className="rounded-xl border border-border dark:border-stone-700 overflow-x-auto">
          <table className="w-full text-sm" data-testid="history-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60 dark:bg-stone-800/60">
              <tr>
                <th className="py-3 px-3">When</th>
                <th>Area</th>
                <th>Category</th>
                <th>Frequency</th>
                <th>Product</th>
                <th>By</th>
                <th>Signed by</th>
                <th>Status</th>
                <th>Verified</th>
                <th>Photo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-border dark:border-stone-700 h-12"
                  data-testid={`history-row-${r.id}`}
                >
                  <td className="px-3 tnum whitespace-nowrap">
                    {r.performed_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="font-medium">{r.task_area || r.area || "—"}</td>
                  <td className="text-stone-600 dark:text-stone-300">
                    {CATEGORY_LABELS[r.task_category] || r.task_category || "—"}
                  </td>
                  <td className="text-stone-600 dark:text-stone-300 capitalize">
                    {r.task_frequency || "—"}
                  </td>
                  <td className="text-stone-600 dark:text-stone-300">{r.products_used || "—"}</td>
                  <td>{r.performed_by_name || "—"}</td>
                  <td data-testid={`history-signed-by-${r.id}`}>
                    {r.signed_by || <span className="text-stone-400">—</span>}
                  </td>
                  <td>
                    <StatusPill status={historyStatus(r)} testId={`history-status-pill-${r.id}`} />
                  </td>
                  <td>
                    {r.verified ? (
                      <div className="flex flex-col gap-0.5" data-testid={`history-verified-${r.id}`}>
                        <span
                          className="inline-flex items-center gap-1 font-semibold"
                          title={
                            r.verify_flagged
                              ? `Flagged by ${r.verified_by_name} · ${r.verify_notes || ""}`
                              : `By ${r.verified_by_name} on ${r.verified_at?.slice(0, 10)}`
                          }
                        >
                          {r.verify_flagged ? (
                            <>
                              <Flag
                                className="h-3.5 w-3.5 text-amber-600 fill-amber-500/30"
                                data-testid={`history-flag-${r.id}`}
                              />
                              <span className="text-amber-800 dark:text-amber-300">
                                {r.verified_by_name}
                              </span>
                            </>
                          ) : (
                            <span className="text-emerald-700 dark:text-emerald-300">
                              ✓ {r.verified_by_name}
                            </span>
                          )}
                        </span>
                        {r.verify_flagged && r.verify_notes && (
                          <span
                            className="text-[11px] text-amber-800 dark:text-amber-200 italic max-w-[16rem] break-words leading-tight"
                            data-testid={`history-verify-comment-${r.id}`}
                          >
                            &ldquo;{r.verify_notes}&rdquo;
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </td>
                  <td>
                    {/* Photo thumbnail — click to open full-size in a
                        modal. We store photos as data: URLs on the
                        cleaning record so no separate fetch is needed. */}
                    {r.photo_data_url ? (
                      <button
                        type="button"
                        onClick={() => setPhotoModal(r.photo_data_url)}
                        data-testid={`history-photo-${r.id}`}
                        className="block h-10 w-10 overflow-hidden rounded border border-border hover:ring-2 hover:ring-primary"
                        title="View photo"
                      >
                        <img
                          src={r.photo_data_url}
                          alt="Cleaning evidence"
                          className="h-full w-full object-cover"
                        />
                      </button>
                    ) : (
                      <span className="text-stone-400">—</span>
                    )}
                  </td>
                  <td className="pr-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {r.missed && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditRow(r)}
                          data-testid={`history-log-missed-${r.id}`}
                          className="h-8 border-rose-300 text-rose-700 hover:bg-rose-50"
                          title="Log this missed cleaning retrospectively"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Log now
                        </Button>
                      )}
                      {!r.missed && !r.verified && (r.completed ?? true) === false && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditRow(r)}
                          data-testid={`history-edit-${r.id}`}
                          className="h-8"
                          title="Amend / complete this record"
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                      )}
                      {!r.missed && r.completed && !r.verified ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setVerifyRow(r)}
                          data-testid={`history-verify-${r.id}`}
                          className="h-8"
                        >
                          <ShieldCheck className="h-3.5 w-3.5 mr-1" /> Verify
                        </Button>
                      ) : (
                        !r.missed &&
                        !((r.completed ?? true) === false) && (
                          <span className="text-stone-300">—</span>
                        )
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Click-to-enlarge photo modal — uses a plain div instead of
          shadcn Dialog so the image gets the full screen real-estate
          without the dialog's padded chrome. */}
      {photoModal && (
        <button
          type="button"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPhotoModal(null)}
          data-testid="history-photo-modal"
          aria-label="Close photo"
        >
          <img
            src={photoModal}
            alt="Cleaning evidence"
            className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
          />
        </button>
      )}
      <SupervisorPinModal
        open={!!verifyRow}
        title="Verify cleaning record"
        description={
          verifyRow
            ? `Sign off on ${verifyRow.task_area || verifyRow.area || "record"} · ${
                verifyRow.performed_at?.slice(0, 10) || ""
              }`
            : ""
        }
        roles={["admin", "dispatcher"]}
        noteLabel="Comment (optional — flags this record)"
        onClose={() => setVerifyRow(null)}
        onVerified={async (_session, note) => {
          const row = verifyRow;
          setVerifyRow(null);
          try {
            await api.post(`/cleaning/${row.id}/verify`, {
              verified: true,
              notes: note || "",
            });
            toast.success(note ? "Verified and flagged for review" : "Verified");
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Verification failed");
          }
        }}
      />

      {/* Edit incomplete records — reuses the shared CompleteDialog which
          pre-fills from `record`. On save we PATCH the existing row
          rather than POSTing a fresh one, so verify state + task_id +
          performed_at stay intact. */}
      <CompleteDialog
        open={!!editRow}
        item={
          editRow
            ? {
                task: {
                  id: editRow.task_id,
                  area_name: editRow.task_area || editRow.area || "record",
                  category: editRow.task_category,
                  frequency: editRow.task_frequency,
                },
              }
            : null
        }
        record={editRow}
        onClose={() => setEditRow(null)}
        onSubmit={async (payload) => {
          try {
            if (editRow?.missed) {
              // Missed occurrence — no real record exists. Create one now.
              await api.post("/cleaning", {
                ...payload,
                task_id: editRow.task_id,
                area: editRow.task_area || editRow.area,
                // Retro-fill performed_at so this record shows up on the
                // day it was actually meant to happen, not today.
                performed_at: editRow.performed_at,
                notes: [
                  payload.notes,
                  "Logged retrospectively — missed on shift day.",
                ]
                  .filter(Boolean)
                  .join(" · "),
              });
              toast.success("Missed cleaning logged");
            } else {
              await api.patch(`/cleaning/${editRow.id}`, {
                completed: payload.completed,
                products_used: payload.products_used,
                issues_found: payload.issues_found,
                corrective_action: payload.corrective_action,
                issue_category: payload.issue_category,
                notes: payload.notes,
                signed_by: payload.signed_by,
                photo_data_url: payload.photo_data_url,
              });
              toast.success("Cleaning record updated");
            }
            setEditRow(null);
            load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
          }
        }}
      />
    </div>
  );
}

export default function Cleaning() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { params, selected } = useLocations();
  const [view, setView] = useState("schedule"); // "schedule" | "history"
  const [frequency, setFrequency] = useState("daily");
  const [schedule, setSchedule] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [completeItem, setCompleteItem] = useState(null);
  const [qrItem, setQrItem] = useState(null);
  const [verifyItem, setVerifyItem] = useState(null);

  const load = useCallback(async () => {
    const [sch, cmp] = await Promise.all([
      api.get(`/cleaning/schedule`, { params: { frequency, ...params() } }),
      api.get(`/cleaning/compliance`, { params: params() }),
    ]);
    setSchedule(sch.data);
    setCompliance(cmp.data);
  }, [frequency, params]);

  useEffect(() => {
    load();
    // `load` is a useCallback that already depends on `frequency` + `params`,
    // so adding it here would loop on every params() identity change.
    // Refreshing on frequency / selected covers the actual user-visible inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frequency, selected]);

  // Deep-link from a QR scan: ?task=<id> opens the complete dialog.
  useEffect(() => {
    if (!schedule) return;
    const u = new URL(window.location.href);
    const tid = u.searchParams.get("task");
    if (tid) {
      const item = schedule.items.find((i) => i.task.id === tid);
      if (item) setCompleteItem(item);
      u.searchParams.delete("task");
      window.history.replaceState({}, "", u.toString());
    }
  }, [schedule]);

  const handleSubmit = async (payload) => {
    try {
      await api.post("/cleaning", payload);
      toast.success("Cleaning record saved");
      setCompleteItem(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const handleVerify = async (note = "") => {
    if (!verifyItem?.latest_record) return;
    try {
      await api.post(`/cleaning/${verifyItem.latest_record.id}/verify`, {
        verified: true,
        notes: note || "",
      });
      toast.success(
        note ? "Verified and flagged for review" : "Cleaning verified by supervisor",
      );
      setVerifyItem(null);
      load();
    } catch {
      toast.error("Verification failed");
    }
  };

  const grouped = (schedule?.items || []).reduce((acc, it) => {
    (acc[it.task.category] ||= []).push(it);
    return acc;
  }, {});
  const isDeep = DEEP_FREQUENCIES.has(frequency);

  // Order the categories: deep cleaning gets its own section first when on a
  // deep-cleaning tab; otherwise daily/weekly categories show.
  const orderedCategories = isDeep
    ? ["deep_cleaning"]
    : ["production", "packing", "baking", "non_production", "cake_baklava"];

  return (
    <div>
      <PageHeader title="Cleaning Compliance" eyebrow="Quality · Sanitation schedule">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-stone-100 dark:bg-stone-800 rounded-full p-1">
            <button
              type="button"
              onClick={() => setView("schedule")}
              data-testid="view-schedule"
              className={`px-3 h-9 rounded-full text-sm font-bold ${
                view === "schedule"
                  ? "bg-white dark:bg-stone-900 shadow"
                  : "text-stone-600 dark:text-stone-300"
              }`}
            >
              <Calendar className="h-4 w-4 inline mr-1.5" /> Schedule
            </button>
            <button
              type="button"
              onClick={() => setView("history")}
              data-testid="view-history"
              className={`px-3 h-9 rounded-full text-sm font-bold ${
                view === "history"
                  ? "bg-white dark:bg-stone-900 shadow"
                  : "text-stone-600 dark:text-stone-300"
              }`}
            >
              <History className="h-4 w-4 inline mr-1.5" /> History
            </button>
          </div>
          {view === "schedule" && (
            <div className="flex items-center gap-2 bg-stone-100 dark:bg-stone-800 rounded-full p-1">
              {FREQUENCIES.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFrequency(f.id)}
                  data-testid={`freq-${f.id}`}
                  className={`px-4 h-9 rounded-full text-sm font-bold ${
                    frequency === f.id
                      ? "bg-white dark:bg-stone-900 shadow"
                      : "text-stone-600 dark:text-stone-300"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </PageHeader>

      {view === "history" ? (
        <HistoryView defaultLocation={selected} />
      ) : (
        <>
          <ComplianceCards data={compliance} frequency={frequency} />

          <div className="mt-6">
            {orderedCategories.map((cat) => {
              const items = grouped[cat] || [];
              if (items.length === 0) return null;
              return (
                <CategoryGroup
                  key={cat}
                  category={cat}
                  items={items}
                  isDeep={isDeep}
                  isAdmin={isAdmin}
                  onAssigned={load}
                  onComplete={(it) => setCompleteItem(it)}
                  onVerify={(it) => setVerifyItem(it)}
                  onShowQR={(it) => setQrItem(it)}
                />
              );
            })}
            {schedule && schedule.items.length === 0 && (
              <Panel title="No tasks scheduled">
                <div className="py-8 text-center text-stone-500 text-sm">
                  No active cleaning tasks for this period. Add some from the Admin panel.
                </div>
              </Panel>
            )}
          </div>
        </>
      )}

      <CompleteDialog
        open={!!completeItem}
        item={completeItem}
        onClose={() => setCompleteItem(null)}
        onSubmit={handleSubmit}
      />
      <QRDialog open={!!qrItem} item={qrItem} onClose={() => setQrItem(null)} />
      <SupervisorPinModal
        open={!!verifyItem}
        title="Supervisor verification"
        description={
          verifyItem
            ? `Confirm cleaning of ${verifyItem.task.area_name} (${
                CATEGORY_LABELS[verifyItem.task.category]
              }).`
            : ""
        }
        roles={["admin", "dispatcher"]}
        noteLabel="Comment (optional — flags this record)"
        onClose={() => setVerifyItem(null)}
        onVerified={(_session, note) => handleVerify(note)}
      />
    </div>
  );
}
